import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { CompanyInfo, Invoice } from '../../types';

// Send invoice email via Brevo (Template 5) - Disabled, Stripe API handles invoicing
export async function sendInvoiceEmail(_companyId: string, _invoice: Invoice): Promise<boolean> {
  return true;
}

// Send invoice reminder email via Brevo - Disabled, Stripe API handles invoicing
export async function sendInvoiceReminderEmail(_companyId: string, _invoice: Invoice, _reminderType: '2_days_before' | 'due_today'): Promise<boolean> {
  return true;
}

// Create a new invoice when a subscription plan is chosen
export async function createInvoiceForCompany(companyId: string, company: CompanyInfo, subscription: any): Promise<Invoice | null> {
  const price = subscription.basePrice !== undefined ? subscription.basePrice : (subscription.price || 0);
  if (price <= 0) {
    console.log(`[Invoice] Skipping invoice creation for company ${companyId} because the subscription price is 0 or less.`);
    return null;
  }

  const invoicesRef = collection(db, 'companies', companyId, 'invoices');
  const snap = await getDocs(invoicesRef);
  const invoiceNumber = 173 + snap.size;

  const createdAt = new Date().toISOString();
  // Net 20 days payment term
  const dueDateObj = new Date();
  dueDateObj.setDate(dueDateObj.getDate() + 20);
  const dueDate = dueDateObj.toISOString();

  // Create an ID using firestore auto-id generator
  const invoiceId = doc(collection(db, `companies/${companyId}/invoices`)).id;

  const invoice: Invoice = {
    id: invoiceId,
    invoiceNumber,
    companyId,
    companyName: company.name || 'Ukendt virksomhed',
    companyCvr: company.cvr || '-',
    billingEmail: company.billingEmail || '',
    address: company.address || '',
    zip: company.zip || '',
    city: company.city || '',
    country: company.country || 'Danmark',
    planId: subscription.planId,
    planName: subscription.planName,
    price: price,
    createdAt,
    dueDate,
    paid: false,
    sentInvoiceEmail: false,
    sentReminder2DaysBefore: false,
    sentReminderDueToday: false,
  };

  await setDoc(doc(db, 'companies', companyId, 'invoices', invoiceId), invoice);

  // Automatically trigger actual dispatch via Brevo Template 5
  try {
    await sendInvoiceEmail(companyId, invoice);
  } catch (mailErr) {
    console.warn("[Billing Auto-Mail] Fejl ved automatisk mailafsendelse af ny faktura:", mailErr);
  }

  return invoice;
}

// Fetch all invoices for a company
export async function fetchInvoices(companyId: string): Promise<Invoice[]> {
  if (!companyId) return [];
  const path = `companies/${companyId}/invoices`;
  try {
    const snap = await getDocs(query(collection(db, 'companies', companyId, 'invoices'), orderBy('invoiceNumber', 'desc')));
    const list: Invoice[] = [];
    snap.forEach((d) => {
      const invData = d.data();
      if (!invData.companyId || invData.companyId === companyId) {
        list.push({ id: d.id, ...invData } as Invoice);
      }
    });
    return list;
  } catch (err) {
    return handleFirestoreError(err, OperationType.LIST, path);
  }
}

// Pay / settle an invoice
export async function payInvoice(companyId: string, invoiceId: string): Promise<void> {
  const path = `companies/${companyId}/invoices/${invoiceId}`;
  try {
    await updateDoc(doc(db, 'companies', companyId, 'invoices', invoiceId), {
      paid: true,
      paidAt: new Date().toISOString()
    });
  } catch (err) {
    return handleFirestoreError(err, OperationType.UPDATE, path);
  }
}

// Update specific fields of an invoice as superadmin
export async function superUpdateInvoice(
  companyId: string, 
  invoiceId: string, 
  data: Partial<Invoice & { 
    creditNote?: boolean; 
    creditNoteId?: string;
    creditNoteAmount?: number;
    creditNoteNote?: string;
    creditNoteIssuedAt?: string; 
    amountPaid?: number; 
  }>
): Promise<void> {
  const path = `companies/${companyId}/invoices/${invoiceId}`;
  try {
    await updateDoc(doc(db, 'companies', companyId, 'invoices', invoiceId), data as any);
  } catch (err) {
    return handleFirestoreError(err, OperationType.UPDATE, path);
  }
}

// Create a standalone credit note document in Firestore
export async function createCreditNoteDocument(companyId: string, creditNoteDoc: any): Promise<void> {
  const path = `companies/${companyId}/invoices/${creditNoteDoc.id}`;
  try {
    await setDoc(doc(db, 'companies', companyId, 'invoices', creditNoteDoc.id), creditNoteDoc);
  } catch (err) {
    return handleFirestoreError(err, OperationType.CREATE, path);
  }
}

// Delete an invoice
export async function deleteInvoice(companyId: string, invoiceId: string): Promise<void> {
  const path = `companies/${companyId}/invoices/${invoiceId}`;
  try {
    await deleteDoc(doc(db, 'companies', companyId, 'invoices', invoiceId));
  } catch (err) {
    return handleFirestoreError(err, OperationType.DELETE, path);
  }
}

// Check if a company has overdue unpaid invoices by more than 2 days - Disabled (Stripe handles payment status)
export async function checkHasOverdueInvoices(_companyId: string): Promise<boolean> {
  return false;
}
